// client/src/pages/RepairsPage.jsx
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Wrench, AlertTriangle, FileText, ChevronRight, Plus, X,
  CalendarClock, Check, Ban, Building2, Loader2, CheckCircle2,
  ArrowRight, Zap, Search,
} from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'
import {
  useIssues, useJobs, useQuotes, useQuote,
  useAddIssue, useVehicles, useRaiseJobRequest,
  useApproveQuote, useRejectQuote,
} from '../api/hooks'
import CalendarPicker from '../components/ui/CalendarPicker'

// ─── Constants ────────────────────────────────────────────────────────────────

const SEVERITY_STYLES = {
  low:      { dot: 'bg-blue-400',   badge: 'bg-blue-500/15 text-blue-400'     },
  medium:   { dot: 'bg-amber-400',  badge: 'bg-amber-500/15 text-amber-400'   },
  high:     { dot: 'bg-orange-400', badge: 'bg-orange-500/15 text-orange-400' },
  critical: { dot: 'bg-red-400',    badge: 'bg-red-500/15 text-red-400'       },
}

const JOB_STATUS_STYLES = {
  requested:         'bg-blue-500/15 text-blue-400',
  quoted:            'bg-purple-500/15 text-purple-400',
  awaiting_approval: 'bg-yellow-500/15 text-yellow-400',
  approved:          'bg-indigo-500/15 text-indigo-400',
  in_progress:       'bg-amber-500/15 text-amber-400',
  waiting_on_parts:  'bg-orange-500/15 text-orange-400',
  completed:         'bg-green-500/15 text-green-400',
  cancelled:         'bg-gray-500/15 text-gray-400',
}

const JOB_STATUS_LABELS = {
  requested:         'Requested',
  quoted:            'Quoted',
  awaiting_approval: 'Awaiting Approval',
  approved:          'Approved',
  in_progress:       'In Progress',
  waiting_on_parts:  'Waiting on Parts',
  completed:         'Completed',
  cancelled:         'Cancelled',
}

const inputCls = `w-full px-3 py-2 text-sm bg-[var(--bg-base)] border border-[var(--border)]
  rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
  focus:outline-none focus:border-indigo-500 transition-colors`

const fmtGBP  = v => `£${Number(v || 0).toFixed(2)}`
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'

// ─── Plate ────────────────────────────────────────────────────────────────────

function Plate({ registration }) {
  return (
    <span className="inline-block font-mono font-bold tracking-widest text-xs
                     bg-yellow-400 text-black px-2 py-0.5 rounded">
      {registration}
    </span>
  )
}

// ─── Section header ───────────────────────────────────────────────────────────

function SectionHeader({ icon: Icon, title, count, colour }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon size={16} className={clsx('shrink-0', colour)} />
      <h2 className={clsx('font-display text-base font-bold', colour || 'text-[var(--text-primary)]')}>
        {title}
      </h2>
      <span className={clsx(
        'px-2 py-0.5 rounded-full text-xs font-medium',
        colour === 'text-blue-400'     ? 'bg-blue-500/15 text-blue-400'     :
        colour === 'text-amber-400'    ? 'bg-amber-500/15 text-amber-400'   :
        colour === 'text-yellow-400'   ? 'bg-yellow-500/15 text-yellow-400' :
        'bg-[var(--bg-overlay)] text-[var(--text-muted)]'
      )}>
        {count}
      </span>
    </div>
  )
}

// ─── Quote approval modal ─────────────────────────────────────────────────────

function QuoteApprovalModal({ quoteId, onClose }) {
  const { data: quote, isLoading } = useQuote(quoteId)
  const approveQuote = useApproveQuote()
  const rejectQuote  = useRejectQuote()
  const [rejecting, setRejecting] = useState(false)
  const [reason,    setReason]    = useState('')

  const handleApprove = async () => {
    if (!window.confirm('Approve this quote?')) return
    try {
      await approveQuote.mutateAsync(quoteId)
      toast.success('Quote approved')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to approve quote')
    }
  }

  const handleReject = async () => {
    try {
      await rejectQuote.mutateAsync({ id: quoteId, reason: reason.trim() || undefined })
      toast.success('Quote declined')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to decline quote')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-2xl bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl
                      animate-scale-in flex flex-col max-h-[90dvh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)] shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">
                {isLoading ? '…' : quote?.quote_number}
              </h2>
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-500/15 text-blue-400">
                Awaiting Approval
              </span>
            </div>
            {quote?.title && (
              <p className="text-sm text-[var(--text-secondary)] mt-0.5">{quote.title}</p>
            )}
          </div>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="p-6 flex items-center justify-center">
              <Loader2 size={20} className="animate-spin text-[var(--text-muted)]" />
            </div>
          ) : !quote ? null : (
            <>
              {/* Vehicle info */}
              <div className="px-6 py-4 bg-[var(--bg-base)] border-b border-[var(--border)]">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[var(--text-muted)] text-xs mb-0.5">Vehicle</p>
                    <p className="font-medium text-[var(--text-primary)]">
                      {quote.registration} — {quote.make} {quote.model}
                    </p>
                  </div>
                  {quote.valid_until && (
                    <div>
                      <p className="text-[var(--text-muted)] text-xs mb-0.5">Valid until</p>
                      <p className="text-[var(--text-primary)]">{fmtDate(quote.valid_until)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Line items */}
              <div className="px-6 py-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-3">
                  Line Items
                </p>
                {(quote.items || []).length === 0 ? (
                  <p className="text-sm text-[var(--text-muted)] italic">No items on this quote.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[var(--text-muted)] text-xs border-b border-[var(--border)]">
                          <th className="text-left pb-2 font-medium">Description</th>
                          <th className="text-left pb-2 font-medium">Type</th>
                          <th className="text-right pb-2 font-medium">Qty</th>
                          <th className="text-right pb-2 font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--border)]">
                        {quote.items.map(item => (
                          <tr key={item.id} className={clsx('text-[var(--text-primary)]', item.already_paid && 'opacity-50')}>
                            <td className="py-2.5 pr-3">
                              <span className={clsx(item.already_paid && 'line-through text-[var(--text-muted)]')}>
                                {item.description}
                              </span>
                              {item.already_paid && (
                                <span className="ml-2 px-1.5 py-0.5 text-xs font-medium bg-green-500/15 text-green-400 rounded-full">
                                  Paid
                                </span>
                              )}
                            </td>
                            <td className="py-2.5 pr-3 capitalize text-[var(--text-secondary)] text-xs">{item.type}</td>
                            <td className="py-2.5 text-right">{Number(item.quantity)}</td>
                            <td className="py-2.5 text-right font-medium">{fmtGBP(item.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {(quote.items || []).length > 0 && (
                  <div className="mt-4 pt-4 border-t border-[var(--border)] space-y-1 text-sm">
                    <div className="flex justify-between text-[var(--text-secondary)]">
                      <span>Subtotal</span><span>{fmtGBP(quote.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-[var(--text-secondary)]">
                      <span>VAT</span><span>{fmtGBP(quote.vat_amount)}</span>
                    </div>
                    {(quote.items || []).some(i => i.already_paid) && (
                      <div className="flex justify-between text-green-400 text-xs pt-1">
                        <span>Parts already paid</span>
                        <span>−{fmtGBP((quote.items || []).filter(i => i.already_paid).reduce((s, i) => s + Number(i.total), 0))}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold text-[var(--text-primary)] text-base pt-1">
                      <span>Total due</span><span>{fmtGBP(quote.total)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Notes */}
              {quote.customer_notes && (
                <div className="px-6 pb-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1">
                    Notes from Garage
                  </p>
                  <p className="text-sm text-[var(--text-primary)] whitespace-pre-line">{quote.customer_notes}</p>
                </div>
              )}

              {/* Rejection input */}
              {rejecting && (
                <div className="px-6 pb-4">
                  <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
                    Reason for declining <span className="text-[var(--text-muted)]">(optional)</span>
                  </label>
                  <textarea
                    rows={3}
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder="Let the garage know why you're declining…"
                    className={clsx(inputCls, 'resize-none')}
                  />
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => setRejecting(false)}
                      className="flex-1 py-2 bg-[var(--bg-overlay)] text-[var(--text-secondary)]
                                 text-sm rounded-lg transition-colors hover:bg-[var(--bg-elevated)]">
                      Back
                    </button>
                    <button onClick={handleReject} disabled={rejectQuote.isPending}
                      className="flex-1 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50
                                 text-white text-sm font-medium rounded-lg transition-colors">
                      {rejectQuote.isPending ? 'Declining…' : 'Confirm Decline'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!isLoading && quote && !rejecting && (
          <div className="px-6 py-4 border-t border-[var(--border)] shrink-0 flex gap-3">
            <button onClick={() => setRejecting(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors border border-[var(--border)]">
              <Ban size={15} />
              Decline
            </button>
            <button onClick={handleApprove} disabled={approveQuote.isPending}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-green-600 hover:bg-green-700
                         disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
              <Check size={15} />
              {approveQuote.isPending ? 'Approving…' : 'Approve Quote'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Raise Job modal ──────────────────────────────────────────────────────────

function RaiseJobModal({ issue, allIssues, onClose }) {
  const raiseJob = useRaiseJobRequest()

  const related = allIssues.filter(i =>
    i.id !== issue.id &&
    i.vehicle_id === issue.vehicle_id &&
    ['open', 'deferred'].includes(i.status)
  )

  const [extraIds,      setExtraIds]      = useState([])
  const [message,       setMessage]       = useState('')
  const [preferredDate, setPreferredDate] = useState('')

  const allSelected = [issue.id, ...extraIds]
  const toggle = id => setExtraIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  const derivedTitle = allSelected.length === 1
    ? `Fix: ${issue.title}`
    : `Fix: ${allSelected.length} issues — ${issue.title}`

  const handleSubmit = async () => {
    try {
      await raiseJob.mutateAsync({
        vehicleId:     issue.vehicle_id,
        title:         derivedTitle,
        description:   message.trim() || null,
        issueIds:      allSelected,
        preferredDate: preferredDate || null,
      })
      toast.success('Job request sent to your garage')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit request')
    }
  }

  const hasGarage = !!issue.primary_garage_id

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">Request Repair</h2>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <p className="text-xs font-medium text-[var(--text-muted)] mb-2">Reporting issue</p>
            <div className="flex items-center gap-2 px-4 py-3 bg-[var(--bg-base)] border border-indigo-500/30 rounded-xl">
              {issue.registration && <Plate registration={issue.registration} />}
              <span className="text-sm font-medium text-[var(--text-primary)]">{issue.title}</span>
            </div>
          </div>

          {related.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[var(--text-muted)] mb-2">Also include from same vehicle</p>
              <div className="space-y-1.5">
                {related.map(i => (
                  <label key={i.id}
                    className="flex items-center gap-3 px-4 py-2.5 bg-[var(--bg-base)]
                               border border-[var(--border)] rounded-xl cursor-pointer
                               hover:bg-[var(--bg-overlay)] transition-colors">
                    <input type="checkbox" checked={extraIds.includes(i.id)} onChange={() => toggle(i.id)}
                      className="accent-indigo-500 shrink-0" />
                    <span className="text-sm text-[var(--text-primary)]">{i.title}</span>
                    <span className={clsx('ml-auto shrink-0 px-1.5 py-0.5 rounded text-xs font-medium',
                      SEVERITY_STYLES[i.severity]?.badge || 'bg-gray-500/15 text-gray-400')}>
                      {i.severity || 'low'}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--bg-base)]">
            <Building2 size={14} className={hasGarage ? 'text-indigo-400' : 'text-[var(--text-muted)]'} />
            {hasGarage ? (
              <div>
                <p className="text-xs text-[var(--text-muted)]">Sending to</p>
                <p className="text-sm font-medium text-[var(--text-primary)]">{issue.primary_garage_name}</p>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium text-amber-400">No garage registered</p>
                <p className="text-xs text-[var(--text-muted)]">Go to Settings → My Garage to register first.</p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5">
              Preferred repair date (optional)
            </label>
            <CalendarPicker value={preferredDate} onChange={setPreferredDate}
              minDate={new Date()} placeholder="Pick a preferred date…" />
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5">
              Message to garage (optional)
            </label>
            <textarea rows={2} value={message} onChange={e => setMessage(e.target.value)}
              placeholder="Any additional details for the garage…"
              className={clsx(inputCls, 'resize-none')} />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button onClick={handleSubmit} disabled={!hasGarage || raiseJob.isPending}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40
                         text-white text-sm font-medium rounded-lg transition-colors
                         flex items-center justify-center gap-2">
              {raiseJob.isPending
                ? <><Loader2 size={14} className="animate-spin" /> Sending…</>
                : <><Wrench size={14} /> Submit Request</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Report Issue modal ───────────────────────────────────────────────────────

function ReportIssueModal({ onClose, onRequestRepair }) {
  const { data: vehicles = [] } = useVehicles()
  const addIssue = useAddIssue()
  const [form, setForm] = useState({
    vehicleId: '', title: '', description: '', severity: 'medium',
    dateIdentified: new Date().toISOString().slice(0, 10),
  })
  const [created, setCreated] = useState(null)
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const issue = await addIssue.mutateAsync({
        vehicleId:      form.vehicleId,
        title:          form.title,
        description:    form.description || null,
        severity:       form.severity    || null,
        dateIdentified: form.dateIdentified || null,
      })
      const vehicle = vehicles.find(v => String(v.id) === String(form.vehicleId))
      setCreated({ ...issue, ...(vehicle && {
        registration:        vehicle.registration,
        make:                vehicle.make,
        model:               vehicle.model,
        primary_garage_id:   vehicle.primary_garage_id,
        primary_garage_name: vehicle.primary_garage_name,
      })})
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to report issue')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">
            {created ? 'Issue Reported' : 'Report Issue'}
          </h2>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <X size={18} />
          </button>
        </div>

        {created ? (
          <div className="p-6 space-y-5">
            <div className="flex items-start gap-3">
              <CheckCircle2 size={22} className="text-green-400 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-[var(--text-primary)]">{created.title}</p>
                <p className="text-xs text-[var(--text-muted)] mt-0.5">Saved as an open issue.</p>
              </div>
            </div>
            <div className="px-4 py-4 bg-indigo-500/5 border border-indigo-500/20 rounded-xl space-y-1">
              <p className="text-sm font-semibold text-[var(--text-primary)]">What happens next?</p>
              <p className="text-sm text-[var(--text-secondary)]">
                To get this fixed you need to request a repair from your garage. They will review
                the issue, raise a job, and send you a quote before any work begins.
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={onClose}
                className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                           text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
                Done
              </button>
              <button
                onClick={() => { onClose(); onRequestRepair(created) }}
                disabled={!created.primary_garage_id}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-indigo-600
                           hover:bg-indigo-700 disabled:opacity-40 text-white text-sm font-medium
                           rounded-lg transition-colors">
                <Wrench size={14} />
                Request Repair
                <ArrowRight size={14} />
              </button>
            </div>
            {!created.primary_garage_id && (
              <p className="text-xs text-[var(--text-muted)] text-center -mt-2">
                No garage linked — go to Settings → My Garage to register first.
              </p>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="flex items-start gap-2.5 px-3 py-3 bg-[var(--bg-base)] border border-[var(--border)] rounded-lg">
              <Wrench size={14} className="text-indigo-400 shrink-0 mt-0.5" />
              <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
                Reporting an issue saves it for your records. To get it fixed, you'll need to
                request a repair from your garage — they'll then raise a job and send you a quote.
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Vehicle</label>
              <select value={form.vehicleId} onChange={set('vehicleId')} className={inputCls} required>
                <option value="">Select a vehicle…</option>
                {vehicles.map(v => (
                  <option key={v.id} value={v.id}>{v.registration} — {v.make} {v.model}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Title</label>
              <input type="text" value={form.title} onChange={set('title')}
                placeholder="e.g. Strange noise from engine" className={inputCls} required />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Description</label>
              <textarea rows={3} value={form.description} onChange={set('description')}
                placeholder="Describe the issue in more detail…"
                className={clsx(inputCls, 'resize-none')} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Severity</label>
                <select value={form.severity} onChange={set('severity')} className={inputCls}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Date identified</label>
                <input type="date" value={form.dateIdentified} onChange={set('dateIdentified')} className={inputCls} />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose}
                className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                           text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={addIssue.isPending}
                className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                           text-white text-sm font-medium rounded-lg transition-colors">
                {addIssue.isPending ? 'Reporting…' : 'Report Issue'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const ACTIVE_JOB_STATUSES = ['requested', 'quoted', 'awaiting_approval', 'approved', 'in_progress', 'waiting_on_parts']

export default function RepairsPage() {
  const navigate = useNavigate()
  const { user }  = useAuth()

  const [approvalQuoteId, setApprovalQuoteId] = useState(null)
  const [raiseIssue,      setRaiseIssue]      = useState(null)
  const [showReport,      setShowReport]      = useState(false)

  const { data: issues   = [], isLoading: iLoading } = useIssues()
  const { data: jobs     = [], isLoading: jLoading } = useJobs()
  const { data: allQuotes = [], isLoading: qLoading } = useQuotes()

  // Needs Action — quotes awaiting customer approval
  const actionableQuotes = allQuotes.filter(q => ['sent', 'viewed'].includes(q.status))

  // In Progress — active jobs
  const activeJobs = jobs.filter(j => ACTIVE_JOB_STATUSES.includes(j.status))

  // Open Issues — reported but not yet linked to an active repair
  const openIssues = issues.filter(i =>
    ['open', 'deferred'].includes(i.status) &&
    (!i.linked_job_status || !ACTIVE_JOB_STATUSES.includes(i.linked_job_status))
  )

  const allEmpty = !iLoading && !jLoading && !qLoading &&
    actionableQuotes.length === 0 && activeJobs.length === 0 && openIssues.length === 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">My Repairs</h1>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Issues, repairs, and quotes in one place.
          </p>
        </div>
        <button
          onClick={() => setShowReport(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700
                     text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus size={16} />
          Report Issue
        </button>
      </div>

      {/* ── Needs Action ── */}
      {(actionableQuotes.length > 0 || qLoading) && (
        <section>
          <SectionHeader icon={Zap} title="Needs Your Action" count={actionableQuotes.length} colour="text-yellow-400" />
          {qLoading ? (
            <div className="space-y-2">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="h-20 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {actionableQuotes.map(q => (
                <button
                  key={q.id}
                  onClick={() => setApprovalQuoteId(q.id)}
                  className="w-full flex items-start gap-4 px-5 py-4 bg-[var(--bg-surface)]
                             border border-yellow-500/30 rounded-xl hover:border-yellow-500/60
                             transition-all text-left group"
                >
                  <FileText size={16} className="text-yellow-400 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      {q.registration && <Plate registration={q.registration} />}
                      <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-yellow-400 transition-colors truncate">
                        {q.title || q.quote_number}
                      </p>
                      <span className="font-mono text-xs text-[var(--text-muted)]">{q.quote_number}</span>
                    </div>
                    <p className="text-xs text-[var(--text-muted)]">
                      {q.make} {q.model}
                      {q.valid_until ? ` · Valid until ${fmtDate(q.valid_until)}` : ''}
                    </p>
                  </div>
                  <div className="shrink-0 flex items-center gap-3">
                    <span className="text-base font-bold text-[var(--text-primary)]">{fmtGBP(q.total)}</span>
                    <span className="px-2 py-1 text-xs font-medium rounded-lg bg-yellow-500/15 text-yellow-400">
                      Approve / Decline
                    </span>
                    <ChevronRight size={14} className="text-[var(--text-muted)] group-hover:text-yellow-400" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ── In Progress ── */}
      {(activeJobs.length > 0 || jLoading) && (
        <section>
          <SectionHeader icon={Wrench} title="In Progress" count={activeJobs.length} colour="text-amber-400" />
          {jLoading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-16 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {activeJobs.map(job => (
                <button
                  key={job.id}
                  onClick={() => navigate(`/repairs/${job.id}`)}
                  className="w-full flex items-center gap-4 px-5 py-4 bg-[var(--bg-surface)]
                             border border-[var(--border)] rounded-xl hover:border-[var(--border-strong)]
                             transition-all text-left group"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      {job.registration && <Plate registration={job.registration} />}
                      <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-indigo-400 transition-colors truncate">
                        {job.title}
                      </p>
                      {job.needs_investigation && (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium
                                         bg-purple-500/15 text-purple-400 border border-purple-500/30 shrink-0">
                          <Search size={10} />
                          Investigation
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[var(--text-muted)] flex-wrap">
                      {job.make && <span>{job.make} {job.model}</span>}
                      {job.scheduled_date && (
                        <span className="flex items-center gap-1 text-indigo-400">
                          <CalendarClock size={11} />
                          {fmtDate(job.scheduled_date)}
                          {job.scheduled_time ? ` · ${job.scheduled_time.slice(0, 5)}` : ''}
                        </span>
                      )}
                      {!job.scheduled_date && job.preferred_date && (
                        <span className="flex items-center gap-1 text-amber-400">
                          <CalendarClock size={11} />
                          Preferred {fmtDate(job.preferred_date)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={clsx('px-2 py-0.5 rounded text-xs font-medium',
                      JOB_STATUS_STYLES[job.status] || 'bg-gray-500/15 text-gray-400')}>
                      {JOB_STATUS_LABELS[job.status] || job.status}
                    </span>
                    <ChevronRight size={15} className="text-[var(--text-muted)] group-hover:text-indigo-400" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ── Open Issues ── */}
      {(openIssues.length > 0 || iLoading) && (
        <section>
          <SectionHeader icon={AlertTriangle} title="Open Issues" count={openIssues.length} colour="text-blue-400" />
          {iLoading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-16 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {openIssues.map(issue => {
                const severity   = issue.severity || 'low'
                const dotStyle   = SEVERITY_STYLES[severity]?.dot || 'bg-gray-400'
                const badgeStyle = SEVERITY_STYLES[severity]?.badge || 'bg-gray-500/15 text-gray-400'
                const canRaise   = !!issue.primary_garage_id

                return (
                  <div
                    key={issue.id}
                    className="flex items-center gap-2 bg-[var(--bg-surface)] border border-[var(--border)]
                               rounded-xl hover:border-[var(--border-strong)] transition-all group"
                  >
                    <button
                      onClick={() => navigate(`/issues/${issue.id}`)}
                      className="flex items-center gap-4 px-5 py-4 flex-1 min-w-0 text-left"
                    >
                      <div className={clsx('w-2.5 h-2.5 rounded-full shrink-0', dotStyle)} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          {issue.registration && <Plate registration={issue.registration} />}
                          <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-indigo-400 transition-colors truncate">
                            {issue.title}
                          </p>
                        </div>
                        <p className="text-xs text-[var(--text-muted)]">
                          {issue.make && `${issue.make} ${issue.model} · `}
                          {new Date(issue.date_identified).toLocaleDateString('en-GB', {
                            day: 'numeric', month: 'short', year: 'numeric',
                          })}
                        </p>
                      </div>
                      <span className={clsx('shrink-0 px-2 py-0.5 rounded text-xs font-medium border capitalize', badgeStyle)}>
                        {severity}
                      </span>
                      <ChevronRight size={15} className="text-[var(--text-muted)] group-hover:text-indigo-400" />
                    </button>
                    {canRaise && (
                      <button
                        onClick={() => setRaiseIssue(issue)}
                        title="Request garage repair"
                        className="shrink-0 mr-3 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                                   text-[var(--text-muted)] hover:text-indigo-400 hover:bg-indigo-500/10
                                   rounded-lg transition-colors border border-transparent hover:border-indigo-500/20"
                      >
                        <Wrench size={12} />
                        Request Fix
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </section>
      )}

      {/* ── All clear ── */}
      {allEmpty && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[var(--bg-surface)] border border-[var(--border)]
                          flex items-center justify-center mb-4">
            <CheckCircle2 size={28} className="text-green-400" />
          </div>
          <h3 className="font-display font-bold text-lg text-[var(--text-primary)] mb-2">All clear</h3>
          <p className="text-sm text-[var(--text-secondary)] max-w-xs">
            No pending quotes, active repairs, or open issues. Use the button above to report a new problem.
          </p>
        </div>
      )}

      {/* ── Modals ── */}
      {approvalQuoteId && (
        <QuoteApprovalModal quoteId={approvalQuoteId} onClose={() => setApprovalQuoteId(null)} />
      )}
      {raiseIssue && (
        <RaiseJobModal
          issue={raiseIssue}
          allIssues={openIssues}
          onClose={() => setRaiseIssue(null)}
        />
      )}
      {showReport && (
        <ReportIssueModal
          onClose={() => setShowReport(false)}
          onRequestRepair={(issue) => { setShowReport(false); setRaiseIssue(issue) }}
        />
      )}
    </div>
  )
}
